/**
 * The plan's approval state, and the buttons that move it along.
 *
 * Sits above the milestone list because it decides whether that list can be
 * touched at all: while a plan is draft, pending or rejected, nothing can be
 * started, held or completed, and the reason needs to be visible rather than
 * discovered by clicking a disabled button.
 */

import { useState } from "react";
import {
  CheckCircle2, ClipboardCheck, Clock, FileEdit, Send, ShieldCheck, XCircle,
} from "lucide-react";

import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../common/ToastMsg";
import { Button } from "../ui/button";
import { Input, Textarea } from "../ui/input";
import {
  ONBOARDED_PLAN_URL, ONBOARDED_PLAN_SUBMIT_URL, ONBOARDED_PLAN_DECISION_URL,
  CHANGE_REQUEST_DECISION_URL,
} from "../../constants/api";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

const LOOK = {
  none: {
    icon: FileEdit, tone: "bg-gray-50 border-gray-200 dark:bg-gray-700/30 dark:border-gray-600",
    title: "No formal plan",
    body: "Milestones here were added ad hoc, so nothing is gated. Apply a template to build an approved plan.",
  },
  draft: {
    icon: FileEdit, tone: "bg-sky-50 border-sky-200 dark:bg-sky-500/10 dark:border-sky-500/30",
    title: "Draft — not submitted",
    body: "Adjust tasks, owners and dates freely, then submit for approval. Work cannot start until it is approved.",
  },
  pending: {
    icon: Clock, tone: "bg-amber-50 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30",
    title: "Waiting for approval",
    body: "The approver has been notified. Work stays locked until they decide.",
  },
  approved: {
    icon: CheckCircle2, tone: "bg-emerald-50 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30",
    title: "Approved",
    body: "Work can run. Changing a due date now needs a change request, because these dates are the commitment.",
  },
  rejected: {
    icon: XCircle, tone: "bg-rose-50 border-rose-200 dark:bg-rose-500/10 dark:border-rose-500/30",
    title: "Rejected",
    body: "Revise the plan and submit it again.",
  },
};

export default function PlanBanner({ clientId, plan, pendingChanges = [], onChanged }) {
  const { create, update } = useApiMethods();
  const [busy, setBusy]         = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [remarks, setRemarks]   = useState("");
  const [kickoff, setKickoff]   = useState(plan?.plan_start_date || "");
  const [decision, setDecision] = useState(null);   // 'approve' | 'reject'

  if (!plan) return null;
  const look = LOOK[plan.plan_status] || LOOK.none;
  const Icon = look.icon;
  const isDraftish = ["none", "draft", "rejected"].includes(plan.plan_status);

  const saveKickoff = async () => {
    if (!kickoff) return ToastMsg("Pick a kickoff date", "error");
    setBusy(true);
    const res = await update(ONBOARDED_PLAN_URL(clientId), { plan_start_date: kickoff });
    setBusy(false);
    if (res?.status === "success") { ToastMsg(res.message || "Kickoff set", "success"); onChanged?.(); }
    else ToastMsg(res?.message || "Failed", "error");
  };

  const submit = async () => {
    setBusy(true);
    const res = await create(ONBOARDED_PLAN_SUBMIT_URL(clientId), { remarks });
    setBusy(false);
    if (res?.status === "success") {
      ToastMsg(res.message || "Submitted", "success");
      setShowSubmit(false); setRemarks("");
      onChanged?.();
    } else ToastMsg(res?.message || "Failed to submit", "error");
  };

  const decide = async () => {
    setBusy(true);
    const res = await create(ONBOARDED_PLAN_DECISION_URL(clientId),
                             { action: decision, remarks });
    setBusy(false);
    if (res?.status === "success") {
      ToastMsg(res.message || "Recorded", "success");
      setDecision(null); setRemarks("");
      onChanged?.();
    } else ToastMsg(res?.message || "Failed", "error");
  };

  const decideChange = async (cid, action) => {
    setBusy(true);
    const res = await create(CHANGE_REQUEST_DECISION_URL(cid), { action });
    setBusy(false);
    if (res?.status === "success") { ToastMsg(res.message || "Recorded", "success"); onChanged?.(); }
    else ToastMsg(res?.message || "Failed", "error");
  };

  return (
    <div className="space-y-3">
      <div className={`rounded-lg border p-4 ${look.tone}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-500 dark:text-gray-300" />
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {look.title}
                {plan.template_name && (
                  <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                    from “{plan.template_name}”
                  </span>
                )}
              </p>
              <p className="mt-0.5 max-w-xl text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                {look.body}
              </p>
              <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                {plan.plan_start_date && <>Kickoff {fmtDate(plan.plan_start_date)}</>}
                {plan.submitted_by && <> · submitted by {plan.submitted_by}</>}
                {plan.approved_by && plan.plan_status !== "pending" && (
                  <> · {plan.plan_status} by {plan.approved_by}</>
                )}
              </p>
              {plan.remarks && (
                <p className="mt-1.5 rounded bg-white/70 px-2 py-1 text-[11px]
                              text-gray-600 dark:bg-gray-800/60 dark:text-gray-300">
                  “{plan.remarks}”
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isDraftish && (
              <>
                <Input type="date" value={kickoff} className="h-8 w-36"
                  onChange={e => setKickoff(e.target.value)} />
                <Button size="sm" variant="outline" onClick={saveKickoff} disabled={busy}>
                  Set kickoff
                </Button>
                <Button size="sm" onClick={() => setShowSubmit(true)} disabled={busy}>
                  <Send className="mr-1 h-3.5 w-3.5" /> Submit for approval
                </Button>
              </>
            )}
            {plan.plan_status === "pending" && plan.can_approve && (
              <>
                <Button size="sm" onClick={() => setDecision("approve")} disabled={busy}>
                  <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Approve
                </Button>
                <Button size="sm" variant="outline" className="text-red-500"
                  onClick={() => setDecision("reject")} disabled={busy}>
                  Reject
                </Button>
              </>
            )}
            {plan.plan_status === "pending" && !plan.can_approve && (
              <span className="rounded-full bg-white/70 px-2.5 py-1 text-[11px] text-gray-500
                               dark:bg-gray-800/60 dark:text-gray-400">
                You are not an approver
              </span>
            )}
          </div>
        </div>
      </div>

      {/* date changes awaiting a decision */}
      {pendingChanges.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4
                        dark:border-amber-500/30 dark:bg-amber-500/10">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
            <ClipboardCheck className="h-4 w-4" />
            {pendingChanges.length} date change{pendingChanges.length === 1 ? "" : "s"} awaiting approval
          </p>
          <ul className="mt-2 space-y-2">
            {pendingChanges.map(cr => (
              <li key={cr.id} className="flex flex-wrap items-center gap-2 text-xs
                                         text-amber-900 dark:text-amber-200">
                <span className="font-medium">{cr.milestone_name}</span>
                <span className="tabular-nums">
                  {fmtDate(cr.previous_due_date)} → <b>{fmtDate(cr.requested_due_date)}</b>
                </span>
                {cr.requested_planned_days != null && (
                  <span className="rounded bg-white/70 px-1.5 dark:bg-gray-800/60">
                    {cr.requested_planned_days}d
                  </span>
                )}
                {cr.reason && <span className="italic opacity-80">“{cr.reason}”</span>}
                <span className="opacity-70">— {cr.requested_by_name || "—"}</span>
                {plan.can_approve && (
                  <span className="ml-auto flex gap-1">
                    <Button size="sm" className="h-6 px-2 text-[11px]"
                      onClick={() => decideChange(cr.id, "approve")} disabled={busy}>
                      Approve
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] text-red-500"
                      onClick={() => decideChange(cr.id, "reject")} disabled={busy}>
                      Reject
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(showSubmit || decision) && (
        <Modal
          title={showSubmit ? "Submit the plan for approval"
                            : decision === "approve" ? "Approve this plan" : "Reject this plan"}
          onClose={() => { setShowSubmit(false); setDecision(null); setRemarks(""); }}>
          <p className="mb-3 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
            {showSubmit
              ? "Every task needs a due date and the kickoff date must be set. The approver is notified straight away."
              : decision === "approve"
                ? "Approving unlocks the workflow — the team can start work, and any later date change will come back to you."
                : "Rejecting sends it back to the submitter to revise."}
          </p>
          <Textarea rows={3} value={remarks} placeholder="Remarks (optional)"
            onChange={e => setRemarks(e.target.value)} />
          <div className="mt-4 flex gap-2">
            <Button size="sm" onClick={showSubmit ? submit : decide} disabled={busy}>
              {showSubmit ? "Submit" : decision === "approve" ? "Approve" : "Reject"}
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => { setShowSubmit(false); setDecision(null); setRemarks(""); }}>
              Cancel
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
    <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-4
                    dark:border-gray-700 dark:bg-gray-800"
      onClick={e => e.stopPropagation()}>
      <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
      {children}
    </div>
  </div>
);
